/**
 * Dosimetry report — Page 2+ component (summary page).
 *
 * Renders PV dose blocks inside a ReportPage.
 * The page-building logic lives in `dosimetry-summary-pages.tsx`
 * so this file only contains React components (required for Vite Fast Refresh).
 */

import { scaleDoseTableByActivity, DOSE_TABLE_INJECTED_ACTIVITY_KEY } from '../../domain/dose-table';
import type { IPerfusedVolume } from '../../domain/perfused-volume';
import { getHepaticReserveStatus } from '../../domain/perfused-volume';
import { SEGMENT_COLORS, ROMAN_NUMERALS, rgbToCss, TUMOR_COLOR } from '../../domain/segment-colors';
import type { DosimetryStateSnapshot } from '../../hooks/use-dosimetry-state';

import { ReportPage } from '@/components/report';
import { cn } from '@/lib/utils';

// ============================================================================
// Public component
// ============================================================================

interface DosimetrySummaryPageProps {
    pvs: IPerfusedVolume[];
    state: DosimetryStateSnapshot;
    pageNumber: number;
    totalPages: number;
    empty?: boolean;
}

export function DosimetrySummaryPage({
    pvs,
    state,
    pageNumber,
    totalPages,
    empty,
}: DosimetrySummaryPageProps) {
    return (
        <ReportPage pageNumber={pageNumber} totalPages={totalPages}>
            <PageHeading />
            {empty ? (
                <div className="flex flex-1 items-center justify-center text-sm text-gray-400">
                    No perfused volumes with dosimetry results available.
                </div>
            ) : (
                <div className="flex flex-col gap-6">
                    {pvs.map((pv) => (
                        <PvDoseBlock key={pv.id} pv={pv} state={state} />
                    ))}
                </div>
            )}
        </ReportPage>
    );
}

// ============================================================================
// Internal components
// ============================================================================

function PageHeading() {
    return (
        <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-300">
            Dosimetry Summary
        </h2>
    );
}

/** A single PV block: name, reserve, segment pills, dose table. */
function PvDoseBlock({ pv, state }: { pv: IPerfusedVolume; state: DosimetryStateSnapshot }) {
    const reserveStatus = getHepaticReserveStatus(pv.hepaticReservePercent);
    const refTable = state.doseTable(pv.id)!;
    const adjusted = state.adjustedDose(pv.id);
    const displayTable = adjusted
        ? scaleDoseTableByActivity(refTable, adjusted.injectedActivityGBq)
        : refTable;

    const activityEntry = displayTable.entries.find((e) => e.key === DOSE_TABLE_INJECTED_ACTIVITY_KEY);
    const doseEntries = displayTable.entries.filter((e) => e.key !== DOSE_TABLE_INJECTED_ACTIVITY_KEY);

    return (
        <div className="rounded border border-gray-200 px-4 py-3">
            {/* Header row: PV name + hepatic reserve */}
            <div className="flex items-center justify-between mb-2 gap-3">
                <h3 className="flex-1 text-sm font-bold text-gray-800">{pv.name}</h3>
            </div>

            {/* Segment pills */}
            <div className="flex flex-wrap gap-1 mb-3">
                {pv.segmentIndices.map((lv) => {
                    const color = SEGMENT_COLORS[lv] ?? [0.6, 0.6, 0.6];
                    return (
                        <span
                            key={lv}
                            className="inline-flex items-center gap-0.5 rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-mono font-medium text-gray-700"
                        >
                            <span
                                className="inline-block h-2 w-2 rounded-sm"
                                style={{ backgroundColor: rgbToCss(color as [number, number, number]) }}
                            />
                            {ROMAN_NUMERALS[lv] ?? lv}
                        </span>
                    );
                })}
                {pv.tumorIndices.map((lv) => {
                    const color = TUMOR_COLOR;
                    return (
                        <span
                            key={lv}
                            className="inline-flex items-center gap-0.5 rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-mono font-medium text-gray-700"
                        >
                            <span
                                className="inline-block h-2 w-2 rounded-sm"
                                style={{ backgroundColor: rgbToCss(color as [number, number, number]) }}
                            />
                            {`Tumor ${lv}`}
                        </span>
                    );
                })}
            </div>

            {/* Stats */}
            <div className="flex items-center gap-3 text-muted-foreground">
                <span className="tabular-nums font-mono">{pv.volumeMl.toFixed(1)} mL</span>
                {pv.tumorVolumeMl > 0 && (
                    <span className="tabular-nums font-mono">
                        {pv.tumorIndices.length} tumor{pv.tumorIndices.length !== 1 ? 's' : ''}
                    </span>
                )}
                <span
                    className={cn(
                        'ml-auto tabular-nums font-mono font-medium ',
                        reserveStatus === 'critical' && 'text-red-500',
                        reserveStatus === 'warning' && 'text-orange-500'
                    )}
                >
                    Hepatic reserve {pv.hepaticReservePercent.toFixed(1)}%
                </span>
            </div>

            {/* Dose table (read-only, inline) */}
            <div className="text-[10px] mt-2">
                {/* Activity row — separated */}
                {activityEntry && (
                    <div className="grid grid-cols-[1fr_auto_auto] gap-x-2 pb-1.5 mb-1.5 border-b-2 border-gray-200">
                        <span className="font-mono font-bold text-gray-700">{activityEntry.label}</span>
                        <span className="text-right tabular-nums font-mono font-bold">
                            {activityEntry.value.toFixed(2)}
                        </span>
                        <span className="font-mono font-bold text-gray-500">{activityEntry.unit}</span>
                    </div>
                )}

                {/* Anatomical dose entries */}
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-2 gap-y-0.5">
                    {doseEntries.map((entry) => (
                        <DoseRow key={entry.key} label={entry.label} value={entry.value} unit={entry.unit} />
                    ))}
                </div>
            </div>
        </div>
    );
}

function DoseRow({ label, value, unit }: { label: string; value: number; unit: string }) {
    const formatted = unit === 'GBq' ? value.toFixed(2) : value.toFixed(4);
    return (
        <>
            <span className="font-mono text-gray-600 truncate">{label}</span>
            <span className="text-right tabular-nums font-mono text-gray-800">{formatted}</span>
            <span className="font-mono text-gray-500">{unit}</span>
        </>
    );
}
